import Big from "big.js";
import { t } from "i18next";
import { CostSummary } from "../../shared/Analytics";
import { ZERO } from "../../shared/bigUtil";
import { Stage } from "../../shared/CaseConfiguration";
import { BargainingData, PartyDescriptor } from "../../shared/CaseModel";
import { WeighedAverage } from "../../shared/Value";
import { PartyValue } from "./PerPartyBarChart";

export interface StageData {
  stage: Stage;
  data: PartyValue[];
}

// Per-stage EV/cost data for a party, before packing and labelling.
export type StageEvData = {
  stage: Stage;
  partyId: string;
  partyName: string;
  expectedValue: number; // expected value (EV) of the party at given stage
  estimatedCost: Big; // Total Estimated Cost at the stage
  incurredCost: Big; // Total Incurred Cost at the stage
  settlementMidpoints: {
    [opponentId: string]: { label: string; midPoint: number };
  };
};

export function getEvChartDataForParty(
  partyId: string,
  perStageData: StageData[],
  parties: { [partyId: string]: PartyDescriptor },
  costsOfParty: CostSummary,
  bargainingData: BargainingData[],
): StageEvData[] {
  const partyName = parties[partyId]?.name ?? partyId;
  let cumulativeEstimated = ZERO;
  const partyBargainingData = bargainingData.find(
    (data) => data.partyId === partyId,
  );

  const totalOpponents = partyBargainingData?.opponents
    ? Object.keys(partyBargainingData.opponents).length
    : 0;

  return perStageData.map(({ stage, data }) => {
    const stageKey = Stage[stage];
    const expectedValue = data.find((p) => p.partyId === partyId)?.value ?? NaN;

    const settlementMidpoints = new Map<
      string,
      { label: string; midPoint: number }
    >();

    if (partyBargainingData?.opponents) {
      if (totalOpponents <= 3) {
        Object.entries(partyBargainingData.opponents).forEach(
          ([opponentId, { baseline }]) => {
            if (!baseline.perStage) return;
            const baselineEntry = baseline.perStage.find(
              (range) => range.stage === stage,
            );
            if (!baselineEntry) return;
            settlementMidpoints.set(opponentId, {
              label: t("case.analytics.ev.chart.settlement.baselineMidpoint", {
                selectedPartyName: parties[partyId].name,
                opponentName: parties[opponentId]?.name ?? opponentId,
              }),
              midPoint: baselineEntry.range.mid,
            });
          },
        );
      } else {
        // If more than 3 opponents, average the midpoints.
        const baselineMidpointAverage = new WeighedAverage();
        Object.entries(partyBargainingData.opponents).forEach(
          ([, { baseline }]) => {
            if (!baseline.perStage) return;
            const baselineEntry = baseline.perStage.find(
              (range) => range.stage === stage,
            );
            if (!baselineEntry) return;
            baselineMidpointAverage.add(baselineEntry.range.mid);
          },
        );

        baselineMidpointAverage.ifDefined((avg) =>
          settlementMidpoints.set("average", {
            label: t(
              "case.analytics.ev.chart.settlement.averageBaselineMidpoint",
            ),
            midPoint: avg.toNumber(),
          }),
        );
      }
    }

    const stageData: StageEvData = {
      stage,
      partyId,
      partyName,
      expectedValue,
      estimatedCost: cumulativeEstimated,
      incurredCost: new Big(costsOfParty.incurredTotal),
      settlementMidpoints: Object.fromEntries(settlementMidpoints),
    };

    cumulativeEstimated = cumulativeEstimated.plus(
      costsOfParty.estimated[stageKey]?.total ?? 0,
    );
    return stageData;
  });
}
